import React from "react";
import { useParams } from "react-router-dom";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import { GetIntellexusDate } from "../../components/forms/common/intellexusDate/IntellexusDateField";
import EntityReferenceComponent from "../../components/entitiesReferences/EntityReferenceComponent";
import ViewPage from "../../components/view/ViewPage";
import ViewFieldCard from "../../components/view/fields/ViewFieldCard";
import ViewTextField from "../../components/view/fields/ViewTextField";
import ViewRichTextField from "../../components/view/fields/ViewRichTextField";
import { useEntityHandlerContext } from "../../controllers/entityController";
import useTeamController from "../../controllers/teamController";
import useTeamLogic from "../../hooks/logic/teamLogic";
import { formatSystemLanguage } from "../../common/formatters/workFormatters";
import { Team } from "../../types/team";
import TeamMembersViewSection from "./sections/view/TeamMembersViewSection";

interface ViewTeamPageProps {
  systemLanguageType: Team["system_language_type"];
}

const DetailField: React.FC<{
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}> = ({ label, value, fullWidth = false }) => (
  <Grid size={fullWidth ? { xs: 12 } : { xs: 12, sm: 6 }}>
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      {value}
    </Box>
  </Grid>
);

const TeamContributionDateContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Team>();
  const team = methods.getValues();

  return (
    <DetailField
      label="Team Contribution Date"
      value={
        <Typography variant="body1">
          {team?.team_contribution_date
            ? GetIntellexusDate(team.team_contribution_date)
            : "N/A"}
        </Typography>
      }
    />
  );
};

const TeamReferencesContent: React.FC<{ showVersion: boolean }> = ({
  showVersion,
}) => {
  const { methods } = useEntityHandlerContext<Team>();
  const team = methods.getValues();

  return (
    <>
      {showVersion ? (
        <DetailField
          label="Version"
          value={
            team?.version ? (
              <EntityReferenceComponent entity={team.version} linkMode="view" />
            ) : (
              <Typography variant="body1">N/A</Typography>
            )
          }
        />
      ) : null}
      <DetailField
        label="Place"
        value={
          team?.place ? (
            <EntityReferenceComponent entity={team.place} linkMode="view" />
          ) : (
            <Typography variant="body1">N/A</Typography>
          )
        }
      />
      <ViewTextField
        name="place_identification_status"
        label="Place Identification Status"
      />
    </>
  );
};

const ViewTeamPage: React.FC<ViewTeamPageProps> = ({ systemLanguageType }) => {
  const { _id } = useParams<{ _id: string }>();
  const teamLogic = useTeamLogic(systemLanguageType);

  if (!_id) {
    throw new Error("Team ID is required for viewing.");
  }

  const controller = useTeamController(_id, systemLanguageType);

  return (
    <ViewPage<Team>
      controller={controller}
      sections={(team) => [<TeamMembersViewSection team={team} />]}
    >
      <ViewFieldCard title="Basic Information">
        <ViewTextField name="published_id" label="Intellexus ID" />
        <ViewTextField name="name" label="Name" />
        <ViewTextField
          name="system_language_type"
          label="System Language"
          textParser={formatSystemLanguage}
        />
        <ViewTextField name="source_language" label="Source Language" />
      </ViewFieldCard>

      <ViewFieldCard title="Contribution">
        <TeamContributionDateContent />
      </ViewFieldCard>

      <ViewFieldCard title="Linked References">
        <TeamReferencesContent showVersion={teamLogic.showField("version")} />
      </ViewFieldCard>

      {teamLogic.showField("revision") ? (
        <ViewFieldCard title="Tibetan Details">
          <ViewTextField name="revision" label="Revision" />
        </ViewFieldCard>
      ) : null}

      {teamLogic.showField("title") || teamLogic.showField("taisho_catalog_no") ? (
        <ViewFieldCard title="Chinese Details">
          {teamLogic.showField("title") ? (
            <ViewTextField name="title" label="Title" />
          ) : null}
          {teamLogic.showField("taisho_catalog_no") ? (
            <ViewTextField
              name="taisho_catalog_no"
              label="Taisho Catalog Number"
            />
          ) : null}
        </ViewFieldCard>
      ) : null}

      <ViewFieldCard title="Notes">
        <ViewRichTextField name="notes" />
      </ViewFieldCard>
    </ViewPage>
  );
};

export default ViewTeamPage;
